import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MfaChallenge } from "@/components/MfaChallenge";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
}

const ProtectedRoute = ({ children, requireSubscription = false }: ProtectedRouteProps) => {
  const { session, loading, subscription, subscriptionLoading } = useAuth();
  const [mfaRequired, setMfaRequired] = useState<string | null>(null); // factorId if MFA needed
  const [mfaChecked, setMfaChecked] = useState(false);

  useEffect(() => {
    if (!session) {
      setMfaChecked(true);
      return;
    }

    // Only check MFA once per session id to avoid re-running on every auth refresh
    const sessionKey = `mfa-checked-${session.user.id}`;
    if (sessionStorage.getItem(sessionKey)) {
      setMfaChecked(true);
      return;
    }

    const checkMfa = async () => {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (data && data.currentLevel === "aal1" && data.nextLevel === "aal2") {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.find((f) => f.status === "verified");
        if (totpFactor) {
          setMfaRequired(totpFactor.id);
        }
      }
      sessionStorage.setItem(sessionKey, "1");
      setMfaChecked(true);
    };
    checkMfa();
  }, [session?.user.id]);

  // Only block on initial auth load. Don't block on subscriptionLoading (which
  // re-runs every 60s and would unmount the dashboard each time).
  if (loading || !mfaChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-caption">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (mfaRequired) {
    return (
      <MfaChallenge
        factorId={mfaRequired}
        onSuccess={() => setMfaRequired(null)}
        onCancel={async () => {
          await supabase.auth.signOut();
          setMfaRequired(null);
        }}
      />
    );
  }

  if (requireSubscription && !subscription.subscribed) {
    return <Navigate to="/subscribe" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
